import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { shouldRefreshToken, isRefreshTokenValid } from "../utils/tokenUtils";
import { refreshToken } from "../services/keycloakService";
import { jwtRefreshToken } from "../services/jwtAuthService";
import { getEnv } from "../envConfig";
import localStorageService from "../services/localStorageService";

const AUTH_METHOD = getEnv().AUTH_METHOD || "keycloak";

export const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const currentUser = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (!currentUser) return;

    // Anonymous users still need background token refresh so their
    // public-view API calls keep working. Only the session-timeout
    // warning dialog (useSessionTimeout) is skipped for them.

    // isCurrent prevents an in-flight checkAndRefreshToken from acting
    // after this effect has been cleaned up (component unmounted or user
    // changed). Without this, the async call started in the OLD session
    // would complete after a fresh login and dispatch LOGOUT on the new session.
    let isCurrent = true;

    const clearAuthAndLogout = () => {
      if (!isCurrent) return;
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      dispatch({ type: "LOGOUT" });
      // AuthGuard will redirect to /login when isAuthenticated becomes false
    };

    const checkAndRefreshToken = async () => {
      if (isRefreshingRef.current) return;

      try {
        isRefreshingRef.current = true;

        const isRefreshValid = await isRefreshTokenValid();
        if (!isCurrent) return;

        if (!isRefreshValid) {
          console.warn("Refresh token expired. Logging out...");
          clearAuthAndLogout();
          return;
        }

        const needsRefresh = await shouldRefreshToken();
        if (!isCurrent) return;

        if (needsRefresh) {
          console.log("Access token expiring soon. Refreshing...");
          try {
            if (AUTH_METHOD === "jwt") {
              await jwtRefreshToken();
            } else {
              await refreshToken();
            }

            if (!isCurrent) return;

            // Sync fresh tokens into Redux so AuthGuard and other selectors
            // always have a non-stale, non-null accessToken.
            const newAccessToken = localStorageService.getItem("access_token");
            const newRefreshToken = localStorageService.getItem("refresh_token");
            dispatch({
              type: "UPDATE_TOKENS",
              payload: { accessToken: newAccessToken, refreshToken: newRefreshToken },
            });
            console.log("Token refreshed and Redux synced.");
          } catch (error) {
            if (!isCurrent) return;
            if (error.message === "REFRESH_TOKEN_EXPIRED") {
              console.error("Refresh token expired during refresh attempt.");
              clearAuthAndLogout();
            } else {
              console.error("Failed to refresh token:", error);
            }
          }
        }
      } catch (error) {
        if (!isCurrent) return;
        console.error("Error in token refresh check:", error);
      } finally {
        isRefreshingRef.current = false;
      }
    };

    checkAndRefreshToken();
    intervalRef.current = setInterval(checkAndRefreshToken, 60000);

    return () => {
      isCurrent = false;
      isRefreshingRef.current = false;
      clearInterval(intervalRef.current);
    };
  }, [dispatch, currentUser]);
};

export default useTokenRefresh;
